/**
 * Unit system utilities (spec §6.3). The internal world unit is **meters**; conversions live here
 * and must only be used at the UI and import/export boundaries — never inside domain/simulation
 * logic.
 */

/** Display unit systems exposed in the UI. */
export type UnitSystem = 'imperial' | 'metric';

/** How a length is formatted for display. */
export type LengthDisplayStyle = 'feet-inches' | 'decimal-feet' | 'meters';

export const METERS_PER_FOOT = 0.3048;
export const FEET_PER_METER = 1 / METERS_PER_FOOT;
export const INCHES_PER_FOOT = 12;
export const METERS_PER_INCH = METERS_PER_FOOT / INCHES_PER_FOOT;

export function feetToMeters(feet: number): number {
  return feet * METERS_PER_FOOT;
}

export function metersToFeet(meters: number): number {
  return meters * FEET_PER_METER;
}

export function inchesToMeters(inches: number): number {
  return inches * METERS_PER_INCH;
}

export function metersToInches(meters: number): number {
  return meters / METERS_PER_INCH;
}

export interface FeetInches {
  feet: number;
  inches: number;
}

/** Convert whole feet + inches to meters. */
export function feetInchesToMeters({ feet, inches }: FeetInches): number {
  return feetToMeters(feet) + inchesToMeters(inches);
}

/**
 * Convert meters to whole feet + residual inches. Inches are rounded to the nearest whole inch and
 * carried into feet when they reach 12 (e.g. 11.6" → +1 ft, 0"). Handles negatives symmetrically.
 */
export function metersToFeetInches(meters: number): FeetInches {
  const sign = meters < 0 ? -1 : 1;
  const totalInches = Math.abs(metersToInches(meters));
  let feet = Math.floor(totalInches / INCHES_PER_FOOT);
  let inches = Math.round(totalInches - feet * INCHES_PER_FOOT);
  if (inches >= INCHES_PER_FOOT) {
    feet += 1;
    inches -= INCHES_PER_FOOT;
  }
  return { feet: sign * feet, inches: sign * inches };
}

/** Resolve the default display style for a unit system. */
export function defaultDisplayStyle(system: UnitSystem): LengthDisplayStyle {
  return system === 'metric' ? 'meters' : 'feet-inches';
}

/** Format a length given in meters for display. */
export function formatLength(
  meters: number,
  style: LengthDisplayStyle,
  fractionDigits = 1,
): string {
  switch (style) {
    case 'meters':
      return `${meters.toFixed(fractionDigits)} m`;
    case 'decimal-feet':
      return `${metersToFeet(meters).toFixed(fractionDigits)} ft`;
    case 'feet-inches': {
      const { feet, inches } = metersToFeetInches(meters);
      return `${feet}' ${Math.abs(inches)}"`;
    }
  }
}

/**
 * Parse a user-entered length string into meters. Accepts:
 *  - `12.5m`, `12.5 m`, `12.5` (meters when metric context) — handled by callers passing style
 *  - `10ft`, `10 ft`, decimal feet
 *  - `10' 6"`, `10'6`, feet-inches
 * Returns `null` when the input cannot be parsed.
 */
export function parseLength(input: string, style: LengthDisplayStyle): number | null {
  const text = input.trim().toLowerCase();
  if (text.length === 0) return null;

  // Feet-inches: 10' 6" or 10'6 or 10 ft 6 in
  const ftInMatch = text.match(/^(-?\d+(?:\.\d+)?)\s*(?:'|ft|feet)\s*(\d+(?:\.\d+)?)?\s*(?:"|in|inch|inches)?$/);
  if (ftInMatch && (style === 'feet-inches' || ftInMatch[2] !== undefined)) {
    const feet = Number.parseFloat(ftInMatch[1] ?? '');
    const inches = ftInMatch[2] !== undefined ? Number.parseFloat(ftInMatch[2]) : 0;
    if (Number.isFinite(feet) && Number.isFinite(inches)) {
      const sign = feet < 0 ? -1 : 1;
      return feetToMeters(feet) + sign * inchesToMeters(inches);
    }
  }

  const numeric = text.match(/^(-?\d+(?:\.\d+)?)\s*(m|meter|meters|ft|feet|foot|'|in|inch|inches|")?$/);
  if (numeric) {
    const value = Number.parseFloat(numeric[1] ?? '');
    if (!Number.isFinite(value)) return null;
    const unit = numeric[2];
    if (unit === 'm' || unit === 'meter' || unit === 'meters') return value;
    if (unit === 'in' || unit === 'inch' || unit === 'inches' || unit === '"') {
      return inchesToMeters(value);
    }
    if (unit === 'ft' || unit === 'feet' || unit === 'foot' || unit === "'") {
      return feetToMeters(value);
    }
    // No unit: infer from the active display style.
    return style === 'meters' ? value : feetToMeters(value);
  }

  return null;
}
